import React, { useState, useEffect } from 'react';
import './App.css';
import TaskContextView from './components/TaskContextView';
import AttentionReport from './components/AttentionReport';
import DemoControls from './components/DemoControls';
import { InboxItem, AttentionSnapshot } from './types';
import { api } from './services/api';

function App() {
  const [activeTab, setActiveTab] = useState<'inbox' | 'report' | 'demo'>('inbox');
  const [inboxItems, setInboxItems] = useState<InboxItem[]>([]);
  const [analytics, setAnalytics] = useState<AttentionSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 120000); // Refresh every 2 minutes
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load inbox items (show up to 100 tasks)
      const inboxData = await api.getInbox(100);
      setInboxItems(inboxData);

      // Load analytics
      const analyticsData = await api.getAnalytics();
      setAnalytics(analyticsData);
    } catch (err) {
      setError('Failed to load data. Please check your connection.');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptTask = async (taskId: string) => {
    try {
      await api.acceptTask(taskId);
      await loadData(); // Refresh after accepting
    } catch (err) {
      console.error('Error accepting task:', err);
    }
  };

  const handleClarifyTask = async (taskId: string) => {
    try {
      const response = await api.clarifyTask(taskId);
      alert(`Clarification requested: ${response.question}`);
      await loadData(); // Refresh after clarifying
    } catch (err) {
      console.error('Error clarifying task:', err);
    }
  };

  const handleExportReport = async (format: 'json' | 'csv') => {
    try {
      const data = await api.exportReport(format);

      // Create download
      const blob = new Blob([format === 'json' ? JSON.stringify(data, null, 2) : data], {
        type: format === 'json' ? 'application/json' : 'text/csv'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attention_tax_report.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting report:', err);
    }
  };

  // Handle loading and error states first
  if (loading && !inboxItems.length && !analytics) {
    return (
      <div className="App app-fullscreen">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading team intelligence...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App app-fullscreen">
        <div className="error">
          <p>{error}</p>
          <button onClick={loadData}>Retry</button>
        </div>
      </div>
    );
  }

  // Both inbox and report use full-screen layout with compact navigation
  return (
    <div className="App app-fullscreen">
      {/* Compact header for all views */}
      <div className="compact-header">
        <nav className="nav-tabs-compact">
          <button
            className={`nav-tab ${activeTab === 'inbox' ? 'active' : ''}`}
            onClick={() => setActiveTab('inbox')}
          >
            Priority Inbox
            {inboxItems.length > 0 && (
              <span className="badge">{inboxItems.length}</span>
            )}
          </button>
          <button
            className={`nav-tab ${activeTab === 'report' ? 'active' : ''}`}
            onClick={() => setActiveTab('report')}
          >
            Attention Tax Report
          </button>
          <button
            className={`nav-tab ${activeTab === 'demo' ? 'active' : ''}`}
            onClick={() => setActiveTab('demo')}
          >
            Demo Controls
          </button>
        </nav>
      </div>

      {/* Render the active view */}
      {activeTab === 'inbox' ? (
        <TaskContextView
          items={inboxItems}
          onAccept={handleAcceptTask}
          onClarify={handleClarifyTask}
        />
      ) : activeTab === 'report' ? (
        analytics && (
          <AttentionReport
            data={analytics}
            onExport={handleExportReport}
          />
        )
      ) : (
        <DemoControls onDataUpdated={loadData} />
      )}
    </div>
  );
}

export default App;